/**
 * FAQ Slider Component
 * Provides responsive navigation and accessibility features for FAQ sections
 */
class FAQSlider {
  constructor(element) {
    this.element = element;
    this.nav = element.querySelector("[data-faq-nav]");
    this.navContainer = element.querySelector("[data-faq-nav-container]");
    this.navList = element.querySelector("[data-faq-nav-list]");
    this.tabs = element.querySelectorAll("[data-faq-tab]");
    this.panels = element.querySelectorAll("[data-faq-panel]");

    // Get settings from data attributes or defaults
    this.centerActiveMobile =
      this.element.dataset.centerActiveMobile !== "false";
    this.stickyNav = this.element.dataset.stickyNav !== "false";

    this.init();
  }

  init() {
    if (!this.tabs.length || !this.panels.length) {
      console.warn("FAQ Slider: No tabs or panels found");
      return;
    }

    this.bindEvents();
    this.setupAccessibility();
    this.setupIntersectionObserver();
    this.setupMomentumScrolling();

    // Don't auto-center on mobile to prevent unwanted scrolling on initialization
    this.updateScrollIndicators();
  }

  bindEvents() {
    // Tab click events
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const tabIndex = tab.dataset.faqTab;
        this.switchTab(tabIndex);
      });
    });

    // Keyboard navigation
    this.navList?.addEventListener("keydown", (e) => {
      this.handleKeyboardNavigation(e);
    });

    // Focus events for better accessibility
    this.tabs.forEach((tab) => {
      tab.addEventListener("focus", () => {
        if (this.isMobile()) {
          // Scroll focused item into view on mobile, but avoid centering the last item
          // to prevent content shift
          const isLastItem = tab === this.tabs[this.tabs.length - 1];
          tab.scrollIntoView({
            inline: isLastItem ? "end" : "center",
            block: "nearest",
            behavior: "smooth",
          });
        }
      });
    });

    // Touch events for mobile
    if ("ontouchstart" in window) {
      this.setupTouchEvents();
    }

    // Resize handler - don't auto-center on resize to prevent unwanted scrolling
    this.resizeHandler = this.debounce(() => {
      // Just update scroll indicators without centering
      this.updateScrollIndicators();
    }, 250);

    window.addEventListener("resize", this.resizeHandler);

    // Handle browser back/forward
    window.addEventListener("popstate", () => {
      this.handleUrlChange();
    });
  }

  setupAccessibility() {
    // Set initial ARIA attributes
    this.tabs.forEach((tab, index) => {
      tab.setAttribute("tabindex", index === 0 ? "0" : "-1");

      // Add role if not present
      if (!tab.getAttribute("role")) {
        tab.setAttribute("role", "tab");
      }
    });

    // Set panel attributes
    this.panels.forEach((panel, index) => {
      if (!panel.getAttribute("role")) {
        panel.setAttribute("role", "tabpanel");
      }
    });
  }

  setupIntersectionObserver() {
    // Observe panels for analytics or other tracking
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const panelId = entry.target.dataset.faqPanel;
              this.onPanelVisible(panelId);
            }
          });
        },
        { threshold: 0.5 }
      );

      this.panels.forEach((panel) => {
        this.observer.observe(panel);
      });
    }
  }

  setupTouchEvents() {
    if (!this.navContainer) return;

    let startX = 0;
    let scrollLeft = 0;
    let isDown = false;
    let hasMoved = false;

    const handleTouchStart = (e) => {
      isDown = true;
      hasMoved = false;
      startX = e.touches[0].pageX - this.navContainer.offsetLeft;
      scrollLeft = this.navContainer.scrollLeft;
      this.navContainer.style.cursor = "grabbing";
    };

    const handleTouchMove = (e) => {
      if (!isDown) return;

      hasMoved = true;
      e.preventDefault();
      const x = e.touches[0].pageX - this.navContainer.offsetLeft;
      const walk = (x - startX) * 2;
      this.navContainer.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = () => {
      isDown = false;
      this.navContainer.style.cursor = "grab";

      // If user didn't drag, allow click events
      if (!hasMoved) {
        return;
      }

      // Snap to nearest item after drag
      setTimeout(() => {
        this.snapToNearestItem();
      }, 100);
    };

    this.navContainer.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    this.navContainer.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    this.navContainer.addEventListener("touchend", handleTouchEnd);

    // Mouse events for desktop drag
    this.navContainer.addEventListener("mousedown", (e) =>
      handleTouchStart({ touches: [e] })
    );
    this.navContainer.addEventListener("mousemove", (e) => {
      if (isDown) handleTouchMove({ touches: [e], preventDefault: () => {} });
    });
    this.navContainer.addEventListener("mouseup", handleTouchEnd);
    this.navContainer.addEventListener("mouseleave", handleTouchEnd);
  }

  switchTab(tabIndex, options = {}) {
    const { updateUrl = true, focus = false } = options;

    // Find the target tab and panel
    const targetTab = this.element.querySelector(
      `[data-faq-tab="${tabIndex}"]`
    );
    const targetPanel = this.element.querySelector(
      `[data-faq-panel="${tabIndex}"]`
    );

    if (!targetTab || !targetPanel) {
      console.warn(`FAQ Slider: Tab or panel with index ${tabIndex} not found`);
      return;
    }

    // Update tabs
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.faqTab === tabIndex;
      tab.classList.toggle("faq-slider__nav-button--active", isActive);
      tab.setAttribute("aria-selected", isActive);
      tab.setAttribute("tabindex", isActive ? "0" : "-1");

      // Update SVG stroke color
      const svg = tab.querySelector("svg path");
      if (svg) {
        svg.setAttribute("stroke", isActive ? "white" : "#0556A3");
      }

      if (isActive) {
        tab.setAttribute("aria-current", "page");
      } else {
        tab.removeAttribute("aria-current");
      }
    });

    // Update panels with animation
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.faqPanel === tabIndex;

      if (isActive) {
        panel.hidden = false;
        panel.setAttribute("aria-hidden", "false");
        panel.classList.add("faq-slider__panel--active");

        // Add entering animation
        if (!options.skipAnimation) {
          panel.classList.add("faq-slider__panel--entering");
          setTimeout(() => {
            panel.classList.remove("faq-slider__panel--entering");
          }, 300);
        }
      } else {
        panel.classList.remove("faq-slider__panel--active");
        panel.setAttribute("aria-hidden", "true");

        // Add exiting animation
        if (!options.skipAnimation) {
          panel.classList.add("faq-slider__panel--exiting");
          setTimeout(() => {
            panel.hidden = true;
            panel.classList.remove("faq-slider__panel--exiting");
          }, 300);
        } else {
          panel.hidden = true;
        }
      }
    });

    // Don't auto-center on mobile to prevent unwanted scrolling
    // Update scroll indicators to maintain proper navigation width
    this.updateScrollIndicators();

    // Focus management
    if (focus) {
      targetTab.focus();
    }

    // Update URL if enabled
    if (updateUrl && history.pushState) {
      const url = new URL(window.location);
      url.searchParams.set("faq", tabIndex);
      history.pushState({ faqTab: tabIndex }, "", url);
    }

    // Announce change to screen readers
    this.announceChange(targetTab.textContent.trim());

    // Trigger custom event
    this.element.dispatchEvent(
      new CustomEvent("faq:tabChanged", {
        detail: { tabIndex, tab: targetTab, panel: targetPanel },
      })
    );
  }

  centerActiveItem() {
    const activeTab = this.element.querySelector(
      ".faq-slider__nav-button--active"
    );
    if (activeTab && this.navContainer) {
      const containerWidth = this.navContainer.clientWidth;
      const tabWidth = activeTab.clientWidth;
      const tabLeft = activeTab.offsetLeft;
      const scrollWidth = this.navContainer.scrollWidth;
      
      // Check if this is the last item
      const isLastItem = activeTab === this.tabs[this.tabs.length - 1];
      const isFirstItem = activeTab === this.tabs[0];
      
      let scrollLeft;
      
      if (isLastItem) {
        // For the last item, scroll to the end to prevent content shift
        scrollLeft = scrollWidth - containerWidth;
      } else if (isFirstItem) {
        // For the first item, scroll to the beginning
        scrollLeft = 0;
      } else {
        // For middle items, center them normally
        scrollLeft = tabLeft - (containerWidth - tabWidth) / 2;
      }

      // Use native scrollTo with smooth behavior for better performance
      this.navContainer.scrollTo({
        left: Math.max(0, Math.min(scrollLeft, scrollWidth - containerWidth)),
        behavior: "smooth",
      });
    }
  }

  snapToNearestItem() {
    if (!this.navContainer || !this.isMobile()) return;

    const containerRect = this.navContainer.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestTab = null;
    let closestDistance = Infinity;

    this.tabs.forEach((tab) => {
      const tabRect = tab.getBoundingClientRect();
      const tabCenter = tabRect.left + tabRect.width / 2;
      const distance = Math.abs(containerCenter - tabCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestTab = tab;
      }
    });

    if (
      closestTab &&
      closestTab.dataset.faqTab !== this.getCurrentActiveTab()
    ) {
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      this.switchTab(closestTab.dataset.faqTab, { focus: false });
    }
  }

  handleKeyboardNavigation(e) {
    const currentTab = document.activeElement;
    const currentIndex = Array.from(this.tabs).indexOf(currentTab);
    let newIndex;

    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case "Home":
        e.preventDefault();
        newIndex = 0;
        break;
      case "End":
        e.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        this.switchTab(currentTab.dataset.faqTab, { focus: true });
        return;
      default:
        return;
    }

    if (newIndex !== undefined && this.tabs[newIndex]) {
      this.tabs[newIndex].focus();
      this.switchTab(this.tabs[newIndex].dataset.faqTab, { focus: false });
    }
  }

  handleUrlChange() {
    const urlParams = new URLSearchParams(window.location.search);
    const faqParam = urlParams.get("faq");

    if (
      faqParam &&
      this.element.querySelector(`[data-faq-tab="${faqParam}"]`)
    ) {
      this.switchTab(faqParam, { updateUrl: false, skipAnimation: true });
    }
  }

  announceChange(tabName) {
    // Create temporary element for screen reader announcements
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "sr-only";
    announcement.textContent = `Switched to ${tabName} section`;

    document.body.appendChild(announcement);

    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }

  onPanelVisible(panelId) {
    // Hook for analytics or other tracking
    this.element.dispatchEvent(
      new CustomEvent("faq:panelVisible", {
        detail: { panelId },
      })
    );
  }

  isMobile() {
    return window.innerWidth <= 768;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Enhanced smooth scrolling with easing
  smoothScrollTo(element, target, duration) {
    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out-cubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      element.scrollLeft = start + change * easeProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  // Get currently active tab index
  getCurrentActiveTab() {
    const activeTab = this.element.querySelector(
      ".faq-slider__nav-button--active"
    );
    return activeTab ? activeTab.dataset.faqTab : null;
  }

  // Update scroll indicators without centering
  updateScrollIndicators() {
    if (!this.navContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.navContainer;
    const isScrollable = scrollWidth > clientWidth;

    if (isScrollable) {
      this.navContainer.classList.remove("no-scroll");
    } else {
      this.navContainer.classList.add("no-scroll");
    }
  }

  // Enhanced touch momentum scrolling
  setupMomentumScrolling() {
    if (!this.navContainer || !this.isMobile()) return;

    let isScrolling = false;
    let scrollTimeout;

    const handleScrollStart = () => {
      isScrolling = true;
    };

    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
      }, 150);
    };

    // Use the class method for updating scroll indicators

    this.navContainer.addEventListener(
      "scroll",
      () => {
        handleScrollStart();
        handleScrollEnd();
        this.updateScrollIndicators();
      },
      { passive: true }
    );

    // Initial check for scroll indicators
    setTimeout(() => this.updateScrollIndicators(), 100);
  }

  destroy() {
    // Cleanup event listeners
    window.removeEventListener("resize", this.resizeHandler);

    if (this.observer) {
      this.observer.disconnect();
    }

    // Remove event listeners from tabs
    this.tabs.forEach((tab) => {
      tab.removeEventListener("click", this.handleTabClick);
    });
  }
}

// Auto-initialize FAQ sliders
function initFAQSliders() {
  const faqSliders = document.querySelectorAll("[data-faq-slider]");
  faqSliders.forEach((slider) => {
    if (!slider.faqSliderInstance) {
      slider.faqSliderInstance = new FAQSlider(slider);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFAQSliders);
} else {
  initFAQSliders();
}

// Re-initialize on page show (for back/forward navigation)
window.addEventListener("pageshow", (e) => {
  if (e.persisted) {
    initFAQSliders();
  }
});

// Export for manual initialization
window.FAQSlider = FAQSlider;
